import { FC, useState, useEffect } from "react"
import { FileText, Download, MoreHorizontal, Eye, Tag } from "lucide-react"
import { Document } from "@/types"
import { cn } from "@/lib/utils"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import {
	useDocumentStages,
	DocumentStagesData,
} from "@/hooks/useDocumentStages"
import ReactMarkdown from "react-markdown"
import { StagesPipeline } from "./StagesPipeline"
import { DocumentSummary } from "./DocumentSummary"
import { UpdateRelevancyModal } from "./UpdateRelevancyModal"
import { DocumentViewerModal, isDocumentViewable } from "./DocumentViewerModal"
import { formatFileSize } from "@/utils/formatFileSize"
import { useQueryClient } from "@tanstack/react-query"
import { useDocumentDownload } from "@/hooks/useDocumentDownload"

type DocumentCardProps = {
	document: Document
	onDelete: () => void
	caseId: string
	isSelected?: boolean
	onSelect?: () => void
	onDeleteMultipleSelected?: () => void
	index?: number
	stagesData?: DocumentStagesData // Optional prop for pre-fetched stages
}

interface SummaryDialogProps {
	summary: string
	open: boolean
	onOpenChange: (open: boolean) => void
}

const SummaryDialog: FC<SummaryDialogProps> = ({
	summary,
	open,
	onOpenChange,
}) => {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="text-2xl font-bold">
						Document Summary
					</DialogTitle>
				</DialogHeader>
				<div className="mt-4 prose prose-sm max-w-none">
					<ReactMarkdown>{summary || "No summary available"}</ReactMarkdown>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export const DocumentCard: FC<DocumentCardProps> = ({
	document: documentData,
	onDelete,
	caseId,
	isSelected,
	onSelect,
	onDeleteMultipleSelected,
	index,
	stagesData: propStagesData,
}) => {
	const queryClient = useQueryClient()
	// Use prop data if provided, otherwise fetch
	const {
		data: fetchedStagesData,
		isLoading,
		error,
	} = useDocumentStages(
		propStagesData ? undefined : caseId,
		propStagesData ? undefined : documentData.id
	)

	const stagesData = propStagesData || fetchedStagesData
	const { downloadDocument, isDownloading } = useDocumentDownload()
	const [fileTypeVisible, setFileTypeVisible] = useState(false)
	const [summaryVisible, setSummaryVisible] = useState(false)
	const [relevanceVisible, setRelevanceVisible] = useState(false)
	const [stagesPipelineVisible, setStagesPipelineVisible] = useState(false)
	const [isUpdateRelevancyModalOpen, setIsUpdateRelevancyModalOpen] =
		useState(false)
	const [isViewerOpen, setIsViewerOpen] = useState(false)

	// Handle 404 errors - document was likely deleted during splitting
	useEffect(() => {
		if (error?.message?.includes("404")) {
			// Invalidate and refetch the case data to get the updated document list
			queryClient.invalidateQueries({ queryKey: ["case", caseId] })
		}
	}, [error, caseId, queryClient])

	// Determine document relevance status
	const isRelevant = stagesData?.stage3?.is_relevant
	const isIrrelevant = stagesData?.stage3?.is_relevant === false

	const hasRelevanceStatus = isRelevant || isIrrelevant

	// Check if all stages are completed
	const isFullyProcessed =
		stagesData &&
		stagesData.stage1.status === "completed" &&
		stagesData.stage2.status === "completed" &&
		stagesData.stage3.status === "completed"

	// Hide stages pipeline after document is fully processed (with delay)
	useEffect(() => {
		if (isFullyProcessed) {
			const timer = setTimeout(() => {
				setStagesPipelineVisible(false)
			}, 2000)
			return () => clearTimeout(timer)
		} else {
			setStagesPipelineVisible(true)
		}
	}, [isFullyProcessed])

	useEffect(() => {
		if (stagesData?.stage1?.understood_file_type) {
			const timer = setTimeout(() => {
				setFileTypeVisible(true)
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [stagesData?.stage1?.understood_file_type])

	useEffect(() => {
		if (stagesData?.stage2?.one_line_summary) {
			const timer = setTimeout(() => {
				setSummaryVisible(true)
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [stagesData?.stage2?.one_line_summary])

	useEffect(() => {
		if (
			stagesData?.stage3?.relevance_reasons ||
			stagesData?.stage3?.irrelevance_reasons
		) {
			const timer = setTimeout(() => {
				setRelevanceVisible(true)
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [
		stagesData?.stage3?.relevance_reasons,
		stagesData?.stage3?.irrelevance_reasons,
	])

	const getDocumentColor = (file_extension: string) => {
		switch (file_extension.toLowerCase()) {
			case "pdf":
				return "text-red-500 bg-red-50"
			case "docx":
				return "text-blue-500 bg-blue-50"
			case "xlsx":
				return "text-green-500 bg-green-50"
			default:
				return "text-gray-500 bg-gray-50"
		}
	}

	const documentTypeColor = getDocumentColor(documentData.file_extension)

	// Get border color based on relevance
	const getRelevanceBorderStyle = () => {
		if (!stagesData?.stage3?.status || stagesData.stage3.status !== "completed")
			return ""
		if (isRelevant) return "border-green-500 border-2"
		if (isIrrelevant) return "border-red-500 border-2"
		return ""
	}

	// Add keydown handler for accessibility
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (onSelect && (event.key === "Enter" || event.key === " ")) {
			event.preventDefault() // Prevent default space bar scroll
			onSelect()
		}
	}

	const handleView = () => {
		setIsViewerOpen(true)
	}

	const handleDownload = () => {
		downloadDocument(caseId, documentData)
	}

	return (
		<>
			<DocumentViewerModal
				isOpen={isViewerOpen}
				onClose={() => setIsViewerOpen(false)}
				document={documentData}
				caseId={caseId}
			/>
			<div
				className={cn(
					"document-card rounded-lg overflow-hidden border transition-colors duration-150 ease-in-out", // Base styles from index.css
					getRelevanceBorderStyle(),
					isSelected ? "bg-red-50 border-red-300" : "bg-card hover:bg-muted/50",
					"hover:-translate-y-1", // Explicit hover effect
					"self-start" // Prevent stretching in grid
				)}
				onClick={onSelect}
				{...(onSelect && {
					role: "button",
					tabIndex: 0,
					onKeyDown: handleKeyDown,
				})}
			>
				{/* Inner div: Handles entrance animation */}
				<div
					className="opacity-0 animate-fade-slide-in w-full" // Removed fixed h-full to allow flexible height
					style={{ animationDelay: `${(index || 0) * 150}ms` }} // Animation style here
				>
					<div className="p-4">
						<div className="flex items-start justify-between mb-3">
							<div className="flex items-center space-x-3 max-w-[calc(100%-3rem)]">
								<div
									className={cn(
										"w-10 h-10 rounded-lg flex items-center justify-center",
										documentTypeColor
									)}
								>
									<FileText className="h-5 w-5" />
								</div>
								<div className="flex-1 min-w-0">
									<h3
										className="font-medium text-foreground truncate"
										title={documentData.name}
									>
										{documentData.name}
									</h3>
									<p className="text-xs text-muted-foreground">
										{documentData.file_extension.toUpperCase()} •{" "}
										{formatFileSize(documentData.size)}
									</p>
								</div>
							</div>

							<div className="flex items-center flex-shrink-0 ml-2 min-w-[2rem]">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-6 w-6 rounded-full flex-shrink-0"
											onClick={(e) => {
												if (onSelect) e.stopPropagation()
											}}
											aria-label={`Actions for ${documentData.name}`}
										>
											<MoreHorizontal className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align="end"
										onClick={(e) => {
											if (onSelect) e.stopPropagation()
										}}
									>
										<DropdownMenuItem
											onSelect={handleView}
											disabled={
												!isDocumentViewable(documentData.file_extension)
											}
										>
											<Eye className="mr-2 h-4 w-4" />
											View
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={handleDownload}
											disabled={isDownloading}
										>
											<Download className="mr-2 h-4 w-4" />
											{isDownloading ? "Downloading..." : "Download"}
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={() => setIsUpdateRelevancyModalOpen(true)}
										>
											<Tag className="mr-2 h-4 w-4" />
											Update Relevancy
										</DropdownMenuItem>
										<DropdownMenuItem
											className="text-destructive focus:text-destructive focus:bg-destructive/10"
											onSelect={onDelete}
										>
											Delete
										</DropdownMenuItem>
										<DropdownMenuItem
											className="text-destructive focus:text-destructive focus:bg-destructive/10"
											onSelect={onDeleteMultipleSelected}
										>
											Delete Multiple
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>

						{/* Relevance Status Badge - when stage 3 is completed */}
						{hasRelevanceStatus &&
							stagesData?.stage3?.status === "completed" && (
								<div
									className={cn(
										"text-xs font-medium py-1 px-2 rounded-md inline-block",
										isRelevant
											? "bg-green-100 text-green-800"
											: "bg-red-100 text-red-800"
									)}
								>
									{isRelevant ? "Relevant" : "Irrelevant"}
								</div>
							)}
						
						{/* Search Relevance Badge */}
						{documentData.relevance_to_search && (
							<div
								className={cn(
									"text-xs font-medium py-1 px-2 rounded-md inline-block mt-2",
									documentData.relevance_to_search === "Exact Match"
										? "bg-purple-100 text-purple-800"
										: documentData.relevance_to_search === "Partial Match"
										? "bg-blue-100 text-blue-800"
										: "bg-gray-100 text-gray-800"
								)}
							>
								{documentData.relevance_to_search}
							</div>
						)}

						{/* Stages Pipeline */}
						<StagesPipeline
							stagesData={stagesData}
							isLoading={isLoading}
							error={error}
							stagesPipelineVisible={stagesPipelineVisible}
						/>

						{stagesData?.stage1?.understood_file_type && (
							<div
								className={cn(
									"mt-2 pt-2 border-t overflow-hidden transition-all duration-700 ease-in-out",
									fileTypeVisible
										? "opacity-100 translate-y-0"
										: "max-h-0 opacity-0 -translate-y-4"
								)}
							>
								<p className="text-xs">
									<span className="text-muted-foreground">File Type:</span>{" "}
									<span className="font-medium">
										{stagesData.stage1.understood_file_type}
									</span>
								</p>
							</div>
						)}

						{stagesData?.stage2?.one_line_summary && (
							<>
								<div
									className={cn(
										"mt-2 pt-2 border-t overflow-hidden transition-all duration-700 ease-in-out",
										summaryVisible
											? "opacity-100 translate-y-0"
											: "max-h-0 opacity-0 -translate-y-4"
									)}
								>
									<div className="text-xs">
										<span className="text-muted-foreground">Summary:</span>{" "}
										<div className="line-clamp-2">
											<DocumentSummary
												stagesData={stagesData}
												preview="one_line"
											/>
										</div>
									</div>
								</div>
							</>
						)}

						{(stagesData?.stage3?.relevance_reasons ||
							stagesData?.stage3?.irrelevance_reasons) && (
							<div
								className={cn(
									"mt-2 pt-2 border-t overflow-hidden transition-all duration-700 ease-in-out",
									relevanceVisible
										? "opacity-100 translate-y-0"
										: "max-h-0 opacity-0 -translate-y-4"
								)}
							>
								{stagesData.stage3.relevance_reasons &&
									stagesData.stage3.relevance_reasons.length > 0 && (
										<div className="mb-1">
											<p className="text-xs">
												<span className="text-muted-foreground">
													Relevance Reasons:
												</span>
											</p>
											<ul className="list-disc list-inside pl-2">
												{stagesData.stage3.relevance_reasons.map(
													(reason, index) => (
														<li
															key={`rel-${index}`}
															className="text-xs font-medium"
														>
															{reason}
														</li>
													)
												)}
											</ul>
										</div>
									)}
								{stagesData.stage3.irrelevance_reasons &&
									stagesData.stage3.irrelevance_reasons.length > 0 && (
										<div>
											<p className="text-xs">
												<span className="text-muted-foreground">
													Irrelevancy Reasons:
												</span>
											</p>
											<ul className="list-disc list-inside pl-2">
												{stagesData.stage3.irrelevance_reasons.map(
													(reason, index) => (
														<li
															key={`irr-${index}`}
															className="text-xs font-medium"
														>
															{reason}
														</li>
													)
												)}
											</ul>
										</div>
									)}
							</div>
						)}

						<div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
							Uploaded: {new Date(documentData.created_at).toLocaleDateString()}
						</div>
					</div>

					<UpdateRelevancyModal
						open={isUpdateRelevancyModalOpen}
						onOpenChange={setIsUpdateRelevancyModalOpen}
						documentId={documentData.id}
						caseId={caseId}
						documentName={documentData.name}
						currentIsRelevant={stagesData?.stage3?.is_relevant || null}
					/>
				</div>
			</div>
		</>
	)
}
